import { inject, injectable } from "inversify";
import type * as THREE from "three";
import { ArcballControls } from "three/examples/jsm/controls/ArcballControls.js";
import { DomElementsService, EventDispatcher, Logger } from "@designboticteam/core";
import { CameraService } from "../../stage/CameraService";
import { isVector3 } from "../../utils/isVector3";
import type { ICameraControlService } from "./ICameraControlService";

@injectable()
class ArcballCameraControlService implements ICameraControlService {
	readonly serviceName = "🎥 ArcballCameraControlService";
	private logger = new Logger(`🎬 WORKSPACE 3D | ${this.serviceName}`, {
		color: "#ffffff",
		backgroundColor: "#0066cc",
	});

	public readonly onCameraUpdated = new EventDispatcher<{ camera: THREE.PerspectiveCamera }>("ArcballCameraControlService.onCameraUpdated");
	public readonly onCameraControlsStatusChanged = new EventDispatcher<{ isEnabled: boolean }>("OrbitCameraControlService.onCameraControlsStatusChanged");

	#controls: ArcballControls | null = null;
	public get controls(): ArcballControls {
		if (this.#controls === null) {
			throw new Error("Controls not initialized");
		}

		return this.#controls;
	}

	constructor(
		@inject(DomElementsService) private readonly domElementsService: DomElementsService,
		@inject(CameraService) private readonly cameraService: CameraService,
	) {}

	public set enabled(value: boolean) {
		if (this.controls) {
			this.controls.enabled = value;
		}
	}

	public initialize(): void {
		const camera = this.cameraService.camera;
		const container = this.domElementsService.containerElement;

		this.#controls = new ArcballControls(camera, container);
		this.controls.enablePan = true;
		this.controls.enableZoom = true;
		this.controls.enableRotate = true;

		this.controls.addEventListener("change", () => {
			if (this.controls) {
				this.onCameraUpdated.dispatch({ camera: this.cameraService.camera });
			}
		});

		this.logger.log("initialized");
	}

	public get targetPoint(): THREE.Vector3 {
		if (this.controls === null) {
			throw new Error("Controls not initialized");
		}

		if ("target" in this.controls === false || isVector3(this.controls.target) === false) {
			throw new Error("ArcballControls is expected to have a target property even though it's missing from type definitions");
		}

		return this.controls.target;
	}

	public setTargetPoint(target: THREE.Vector3): void {
		if (this.controls === null) {
			throw new Error("Controls not initialized");
		}

		this.targetPoint.copy(target);
		this.controls.update();
	}

	public enable(): void {
		this.controls.enabled = true;
		this.onCameraControlsStatusChanged.dispatch({ isEnabled: true });
	}

	public disable(): void {
		this.controls.enabled = false;
		this.onCameraControlsStatusChanged.dispatch({ isEnabled: false });
	}

	public dispose(): void {
		this.onCameraUpdated.clear();
		this.controls?.dispose();
		this.#controls = null;
	}
}

export { ArcballCameraControlService };
